// Shared LLM streaming client. All requests go through the FastAPI backend
// proxy (/api/ollama/*, /api/claude/*) so remote browsers don't need direct
// access to localhost:11434. Both proxies emit the same NDJSON shape
// ({ message: { content } } lines, then { done: true }; errors as { error }),
// so one reader serves both backends.

export const OLLAMA_BASE = '/api/ollama';
export const CLAUDE_BASE = '/api/claude';

// The model used for one-click coaching flows (no model picker shown to the
// user). Matched by prefix against /ollama/tags so quantisation-suffixed
// variants (mistral-small3.2:24b-instruct-q4_K_M, …) also hit. This is the
// default; the live value is served by GET /api/config (OLLAMA_COACH_MODEL).
export const PREFERRED_COACH_MODEL = 'mistral-small3.2:24b';

// Stream one NDJSON chat request, calling onDelta for each text fragment.
// `label` names the backend in error messages ('Ollama' / 'Claude').
export async function streamNdjsonChat(url, body, signal, onDelta, label = 'Ollama') {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error(`${label} returned ${res.status}`);

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';   // carries incomplete lines across chunks

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Split on newlines — each complete line is a JSON object
    const lines = buffer.split('\n');
    // Keep the last (possibly incomplete) line in the buffer
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) continue;
      let obj;
      try {
        obj = JSON.parse(line);
      } catch {
        continue;   // malformed line — skip
      }
      // Backend proxy surfaces errors as { error: "..." }
      if (obj.error) throw new Error(obj.error);
      if (obj.message?.content) onDelta(obj.message.content);
      if (obj.done) return;
    }
  }
}

// num_ctx must always hold the whole prompt plus the generation: if it
// doesn't, llama.cpp evicts from the START of the context — i.e. the system
// prompt — and the model degenerates into echoing the briefing or producing
// garbled output. So grow num_ctx for larger prompts (~3 chars/token is a
// safe over-estimate) with a caller-selectable floor (8192 by default; the
// longer Deep Analysis report reserves 16384).
export function ollamaGenOptions(system, user, numPredict = 1500, minContext = 8192) {
  const numCtx = Math.max(minContext,
    Math.ceil((system.length + user.length) / 3) + numPredict + 256);
  // Fixed seed + temperature 0 so repeat sends are reproducible.
  return { num_predict: numPredict, num_ctx: numCtx, temperature: 0.0, seed: 7 };
}

export function estimateChatTokens(messages) {
  return (messages || []).reduce((total, message) => (
    total + Math.ceil(String(message?.content || '').length / 3) + 8
  ), 0);
}

export function ollamaMessageOptions(
  messages,
  numPredict = 700,
  minContext = 32768,
  maxContext = 65536,
) {
  const needed = estimateChatTokens(messages) + numPredict + 256;
  return {
    num_predict: numPredict,
    num_ctx: Math.min(maxContext, Math.max(minContext, needed)),
    temperature: 0.0,
    seed: 7,
  };
}

// Stream one Ollama chat call with the standard generation options.
export async function streamOllamaChat({
  model, system, user, numPredict = 1500, minContext = 8192, signal, onToken,
}) {
  const body = {
    model,
    stream: true,
    options: ollamaGenOptions(system, user, numPredict, minContext),
    messages: [
      { role: 'system', content: system },
      { role: 'user',   content: user   },
    ],
  };
  await streamNdjsonChat(`${OLLAMA_BASE}/chat`, body, signal, onToken);
}

export async function streamOllamaMessages({
  model,
  messages,
  numPredict = 700,
  minContext = 32768,
  maxContext = 65536,
  signal,
  onToken,
}) {
  const body = {
    model,
    stream: true,
    options: ollamaMessageOptions(messages, numPredict, minContext, maxContext),
    messages,
  };
  await streamNdjsonChat(`${OLLAMA_BASE}/chat`, body, signal, onToken);
}

// List locally installed Ollama models: [{ name, size_gb }].
export async function fetchOllamaModels() {
  const r = await fetch(`${OLLAMA_BASE}/tags`);
  if (!r.ok) throw new Error(`Ollama returned ${r.status}`);
  const data = await r.json();
  return (data?.models ?? []).map(m => ({
    name:    m.name,
    size_gb: m.size ? (m.size / 1e9).toFixed(1) : null,
  }));
}

// Pick the model for one-click coaching: the preferred coach model when
// installed, else the first available.
export function pickCoachModel(models, servedModel = '') {
  const wanted = servedModel || PREFERRED_COACH_MODEL;
  const hit = models.find(m => m.name.startsWith(wanted));
  return hit?.name ?? models[0]?.name ?? null;
}
